import React from 'react';
import PeopleActivityScreen, { ActivitySession } from '../PeopleActivityScreen';
import { useRoutineObservation, OBS_STEPS, DURING_BANDS, ObsStep } from './useRoutineObservation';
import StepProgress from './StepProgress';
import { StageCard, DuringCard, ObsNoteBar } from './ObservationStageCard';
import LastTwelveWeeks from '../common/components/LastTwelveWeeks';
import BalancedFeedback from '../common/components/BalancedFeedback';
import CoachingHistory from '../common/components/CoachingHistory';
import NewAction from '../common/components/NewAction';
import FinalSignatures from '../common/components/FinalSignatures';
import { discardActivity } from '../../peopleHubApi';

// Re-export so App.tsx / PeopleHub/index.tsx can import ActivitySession from here
export type { ActivitySession };

// ─── Props ────────────────────────────────────────────────────────────────────
interface ObservationScreenProps {
  session: ActivitySession;
  onExit: () => void;
  onComplete: () => void;
}

// ─── Screen ───────────────────────────────────────────────────────────────────

/**
 * Entry point for Routine Observation and Full Observation.
 *
 * Wires:
 *   useRoutineObservation — all state/logic
 *   PeopleActivityScreen  — shared shell (scrollable body + fixed footer)
 *
 * Steps 0-2: metrics + stepper + stage card (before/during/after)
 * Step 3:    metrics + stepper + coaching section (same as PulseCheck)
 */
const ObservationScreen: React.FC<ObservationScreenProps> = ({
  session,
  onExit,
  onComplete,
}) => {
  const {
    metricRows,
    periodMetricRows,
    step, reached, goNext, goBack, goToStep,
    allQuestions, checkedItems, addInputs, questionsLoading,
    collapsedBands, toggleItem, addCustomItem, setAddInput, toggleBand, checkedCount,
    getKfaPhotoCount, getKfaPhotos, setKfaPhotos,
    feedback, setFeedback,
    openItems, historyItems,
    actionDraft, setActionDraft,
    sigData, setSigData,
    isSignatureUploading, setIsSignatureUploading,
    handleFollowUpResult, handleFollowUpFeedback,
    newActionGated, gateMessage,
    validationError, canComplete, isSubmitting,
    handleSaveDraft, handleComplete,
  } = useRoutineObservation(session);

  // ── Footer labels ────────────────────────────────────────────────────────
  const backLabel   = step > 0 ? `Back: ${OBS_STEPS[step - 1].title}` : undefined;
  const nextLabel   = step < 3 ? `Next: ${OBS_STEPS[step + 1].title}` : undefined;

  // ── Signers for FinalSignatures ──────────────────────────────────────────
  const signers = [
    { role: 'Employee', expectedName: session.employeeName },
    { role: 'Manager',  expectedName: session.managerName  },
  ];

  // ── During bands with derived counts ────────────────────────────────────
  const isReadOnly = session.isReadOnly === true;
  const duringBands = DURING_BANDS.map((b) => ({
    ...b,
    questions:    allQuestions[b.key] ?? [],
    checkedCount: checkedCount(b.key),
  }));

  // Discard the in-progress activity row when exiting directly on the first stage
  // (Before Activity). Later stages show the Leave Activity modal instead, whose
  // Save-Draft-and-Leave / Leave-Without-Saving buttons never discard the activity.
  // Only a brand-new, untouched activity is discarded; anything already saved/resumed stays.
  const handleDiscardExit = async () => {
    if (session.activityId != null && session.status === 'NEW') {
      try {
        await discardActivity(session.activityId);
      } catch (err) {
        console.warn('Failed to discard observation activity:', err);
      }
    }
    onExit();
  };

  return (
    <PeopleActivityScreen
      session={session}
      onExit={onExit}
      onDiscardExit={handleDiscardExit}
      isFirstStage={step === 0}
      onComplete={() => handleComplete(onComplete)}
      onSaveDraft={handleSaveDraft}
      canComplete={canComplete}
      isSubmitting={isSubmitting}
      validationError={step === 3 ? validationError : null}
      // Observation footer mode
      backLabel={backLabel}
      onBack={step > 0 ? goBack : undefined}
      onNext={step < 3 ? goNext : undefined}
      nextLabel={nextLabel}
    >
      {/* ── Metrics (always visible, no 12-Wk Avg / Weeks Met columns) ── */}
      <LastTwelveWeeks
        title="Last Twelve Weeks"
        rows={metricRows}
        periodRows={periodMetricRows}
        periodTitle="Last Twelve Periods"
        activityType="routine_observation"
      />

      {/* ── Step progress tabs ─────────────────────────────────────────── */}
      <StepProgress
        step={step}
        reached={reached}
        onStep={(s: ObsStep) => goToStep(s)}
      />

      {/* ── Steps 0-2: observation note + stage card ──────────────────── */}
      {step < 3 && !isReadOnly && <ObsNoteBar />}

      {/* Step 0 — Before Activity */}
      {step === 0 && (
        <StageCard
          stageKey="before"
          stepNumber={1}
          title="Before Activity"
          subtitle="Preparation and planning before work begins"
          category="before"
          questions={allQuestions.before ?? []}
          checkedItems={checkedItems}
          addInput={addInputs.before ?? ''}
          onToggle={toggleItem}
          onAddInputChange={(val) => setAddInput('before', val)}
          onAddItem={() => addCustomItem('before')}
          getPhotoCount={getKfaPhotoCount}
          getPhotos={getKfaPhotos}
          onSavePhotos={setKfaPhotos}
          isLoading={questionsLoading}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Step 1 — During Activity (3 accordion bands) */}
      {step === 1 && (
        <DuringCard
          bands={duringBands}
          collapsedBands={collapsedBands}
          checkedItems={checkedItems}
          addInputs={addInputs}
          onToggleBand={toggleBand}
          onToggleItem={toggleItem}
          onAddInputChange={setAddInput}
          onAddItem={addCustomItem}
          getPhotoCount={getKfaPhotoCount}
          getPhotos={getKfaPhotos}
          onSavePhotos={setKfaPhotos}
          isLoading={questionsLoading}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Step 2 — After Activity */}
      {step === 2 && (
        <StageCard
          stageKey="after"
          stepNumber={3}
          title="After Activity"
          subtitle="Completion and follow-up after work"
          category="after"
          questions={allQuestions.after ?? []}
          checkedItems={checkedItems}
          addInput={addInputs.after ?? ''}
          onToggle={toggleItem}
          onAddInputChange={(val) => setAddInput('after', val)}
          onAddItem={() => addCustomItem('after')}
          getPhotoCount={getKfaPhotoCount}
          getPhotos={getKfaPhotos}
          onSavePhotos={setKfaPhotos}
          isLoading={questionsLoading}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Step 3 — Coaching (same sections as PulseCheck) */}
      {step === 3 && (
        <>
          <BalancedFeedback
            feedback={feedback}
            onChange={setFeedback}
            isReadOnly={isReadOnly}
          />

          <CoachingHistory
            openItems={openItems}
            historyItems={historyItems}
            activityType="routine_observation"
            isReadOnly={isReadOnly}
            onFollowUpResultChange={handleFollowUpResult}
            onFollowUpFeedbackChange={handleFollowUpFeedback}
          />

          {!isReadOnly && (
            <NewAction
              draft={actionDraft}
              onDraftChange={setActionDraft}
              activityType="routine_observation"
              isGated={newActionGated}
              gateMessage={gateMessage}
              noOpenItem={openItems.length === 0}
            />
          )}

          <FinalSignatures
            signatureData={sigData}
            onSignatureChange={setSigData}
            signers={signers}
            activityType="routine_observation"
            isReadOnly={isReadOnly}
            onUploadingChange={setIsSignatureUploading}
          />
        </>
      )}
    </PeopleActivityScreen>
  );
};

export default ObservationScreen;
